import React, { useContext } from "react";
import { v1 as uuidv1 } from "uuid";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import { useTranslate, $$ } from "../../../helpers";
import { MedigapPlanPolicy } from "../utils/plans";
import planDataUtils from "../../../helpers/planDataUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getMedigapPriceRange } from "../utils/medigapUtilities";
import { AppContext } from "../../../app/store";
import HouseholdDiscountHelpDrawer from "../HelpDrawers/HouseholdDiscountHelpDrawer";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";
import { linkToMedigapCostsOfPolicies } from "../../../helpers/CONSTANTS";

export const medigapPolicyCardPricingTestId =
  "medigap-policy-card-pricing-test-id";
export const medigapPolicyCardCompanyNameTestId =
  "medigap-policy-card-company-name-test-id";

const MedigapPolicyCard: React.FunctionComponent<{
  policy: MedigapPlanPolicy;
}> = ({ policy }) => {
  const t = useTranslate();
  const {
    state: { language },
    dispatch,
  } = useContext(AppContext);
  const flags = useFlags();
  const { mctCurrentPlanYear, tmpFeEnableMedigapPolicyPricing } = flags;
  const cardId = uuidv1();
  const partBPremium = planDataUtils.getPartBPremiumForYear(
    mctCurrentPlanYear || new Date().getFullYear().toString()
  );

  const focusOnDemographicComponent = (): void => {
    const focusElement = document.querySelector(
      ".AccuratePriceContainer"
    ) as HTMLInputElement;
    focusElement.focus();
  };

  return (
    <Card className="MedigapPolicyCard e2e-medigap-policy-card">
      <section className="MedigapPolicyCard__header">
        <h2
          className="e2e-policy-company"
          id={cardId}
          data-testid={medigapPolicyCardCompanyNameTestId}
        >
          {t(policy.company)}
        </h2>
      </section>
      <div className="MedigapPolicyCard__body">
        {tmpFeEnableMedigapPolicyPricing && (
          <>
            <section className="MedigapPolicyCard__section_column--left">
              <h3 className="MedigapPolicyCard__section_heading ds-u-margin-bottom--0">
                {t("medigap.card.section_heading.monthly_cost")}
              </h3>

              <>
                <span
                  className="ds-u-font-size--title e2e-monthly-cost"
                  data-testid={medigapPolicyCardPricingTestId}
                >
                  {getMedigapPriceRange(
                    policy.monthly_rate_min,
                    policy.monthly_rate_max
                  )}
                </span>
              </>
              <p>
                {t("medigap.policies.cost_estimate")}{" "}
                {t("medigap.policies.contact_company_for_quote")}
              </p>
              <div className="mct-hide-print">
                <HouseholdDiscountHelpDrawer />
              </div>
              <p>
                {t("plan_card.does_not_include")}:
                <br />
                {$$(partBPremium)} {t("plan_terms.partb_premium")}
              </p>
              <div className="ds-u-margin-top--3 mct-hide-print">
                <Button
                  variation="ghost"
                  className="ds-u-padding--0 ds-u-text-align--left"
                  onClick={() => {
                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                          buttonType: AnalyticsButtonType.BUTTON,
                          text: t("medigap.get_more_accurate_price"),
                        },
                      },
                    });
                    focusOnDemographicComponent();
                  }}
                >
                  {t("medigap.get_more_accurate_price")}
                </Button>
              </div>
            </section>
            <div className="MedigapPolicyCard__divider"></div>
          </>
        )}

        <section className="MedigapPolicyCard__section_column--middle">
          <h3 className="MedigapPolicyCard__section_heading">
            {t("medigap.policies.contact_company")}
          </h3>
          <div className="MedigapPolicyCard__company_field">
            <h5>{t("medigap.address")}</h5>
            {policy.address}
          </div>
          <div className="MedigapPolicyCard__company_field">
            <h5 className="ds-u-margin-bottom--0">
              {t("medigap.phone_number")}:
            </h5>
            <a
              aria-describedby={cardId}
              href={`tel:${policy.phone_number}`}
              onClick={(): void => {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Step 2: Pick your policy",
                    event_label: `Result: Phone ${policy.company}`,
                  },
                });
              }}
            >
              {policy.phone_number}
            </a>
          </div>
          <div className="MedigapPolicyCard__company_field">
            <h5 className="ds-u-margin-bottom--0">{t("medigap.website")}:</h5>
            <a
              aria-describedby={cardId}
              href={policy.website}
              className="mct-hide-print"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(): void => {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Step 2: Pick your policy",
                    event_label: `Result: Website ${policy.company}`,
                  },
                });
              }}
            >
              {t("medigap.policies.visit_company_website")}
            </a>
            <span className="mct-print-only">{policy.website}</span>
          </div>
        </section>
        <div className="MedigapPolicyCard__divider"></div>
        <section className="MedigapPolicyCard__section_column--right">
          <h3 className="MedigapPolicyCard__section_heading e2e-policy-rate-type">
            {t(`medigap.rate_type.heading.${policy.rate_type.toLowerCase()}`)}
          </h3>
          <p>
            {t(
              `medigap.rate_type.description.${policy.rate_type.toLowerCase()}`
            )}
          </p>
          <p>
            <a
              aria-describedby={cardId}
              href={linkToMedigapCostsOfPolicies(language)}
              className="mct-hide-print"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(): void => {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Step 2: Pick your policy",
                    event_label: "Learn More: Learn about costs",
                  },
                });
              }}
            >
              {t("medigap.policies.learn_about_costs")}
            </a>
          </p>
        </section>
      </div>
    </Card>
  );
};

export default MedigapPolicyCard;
